import { useRef, useState } from "react";
import classnames from "classnames";

import { Action, Category } from "@/constants/events";
import { Store } from "@/custom-types/StoreLocator";
import useDomainCountryCode from "@/hooks/useDomainCountryCode";
import { useEventTracker } from "@/hooks/useEventTracker";
import { getRoundedDistance } from "@/utils/distance";

import DispensaryCard from "@/components/botanic/DispensaryCard";
import ChevronIcon from "@/components/Icons/ChevronIcon";
import TrackImpression from "@/components/TrackImpression";

const RecentlyViewedCarousel: React.FC<{
  stores: Store[];
}> = ({ stores }) => {
  const countryCode = useDomainCountryCode();
  const [currIdx, setCurrIdx] = useState(0);
  const { publishEvent } = useEventTracker();
  const isFirstIdx = currIdx === 0;
  const isLastIdx = currIdx === stores?.length - 1;
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const handlePrev = () => {
    const wrapper = wrapperRef.current;
    if (!wrapper) {
      return null;
    }
    wrapper.scrollLeft -= 200;
    if (!isFirstIdx) {
      setCurrIdx(currIdx - 1);
    }
  };

  const handleNext = () => {
    const wrapper = wrapperRef.current;
    if (!wrapper) {
      return null;
    }
    wrapper.scrollLeft += 200;
    if (!isLastIdx) {
      setCurrIdx(currIdx + 1);
    }
  };

  return (
    <div className="flex flex-col gap-sm">
      <div className="flex justify-between items-center w-full">
        <h2 className="text-xs">RECENTLY VIEWED</h2>
        <div className="hidden md:block">
          <button
            aria-label="Previous Recently Viewed Store"
            className="rounded-full shadow-low mr-sm"
            onClick={handlePrev}
          >
            <ChevronIcon
              className={classnames({
                "text-deep-green-40": isFirstIdx,
                "text-green": !isFirstIdx,
              })}
              height="24"
              width="24"
              direction="left"
            />
          </button>
          <button
            aria-label="Next Recently Viewed Store"
            className="rounded-full shadow-low ml-sm"
            onClick={handleNext}
          >
            <ChevronIcon
              className={classnames({
                "text-deep-green-40": isLastIdx,
                "text-green": !isLastIdx,
              })}
              height="24"
              width="24"
              direction="right"
            />
          </button>
        </div>
      </div>
      <div
        ref={wrapperRef}
        className="flex overflow-x-auto overflow-y-hidden snap-x snap-mandatory no-scrollbar scroll-py-2 gap-lg"
      >
        {stores?.map((store, idx) => (
          <TrackImpression
            key={store.id}
            label={`recently viewed - slot ${idx + 1}`}
            category={Category.finder}
            customDimensions={{
              dispensaryDistance: getRoundedDistance(
                store.distanceMi,
                countryCode,
              ),
              dispensaryId: store.id,
            }}
          >
            <DispensaryCard
              currentStatus={store.openStatus}
              href={store.path}
              onClick={() =>
                publishEvent({
                  action: Action.click,
                  category: Category.finder,
                  dispensaryId: store.id,
                  label: `recently viewed - slot ${idx + 1}`,
                })
              }
              dispensaryId={store.id}
              inStoreCartEnabled={store.configurations.inStoreCartEnabled}
              preorderConfigured={store.configurations.preorderEnabled}
              name={store.name}
              timeZone={store.timeZone || undefined}
              distanceMi={store.distanceMi || 0}
              scheduleType="store"
              slug={store.slug}
              className="border-light-grey border shadow-none mr-md snap-center w-[278px] min-w-[278px]"
              key={idx}
              variant="info"
              rating={store.reviewRating}
              reviewCount={store.reviewCount}
              disableWrappingFulfillmentDetails={true}
              mainTag={store.mainTag}
            />
          </TrackImpression>
        ))}
      </div>
    </div>
  );
};

export default RecentlyViewedCarousel;
